import React, { useCallback, useEffect, useState } from 'react';
import { hr } from '../face/hrApi';
import FaceEnrolTab from './face/FaceEnrolTab';
import FaceDevicesTab from './face/FaceDevicesTab';
import FaceLogTab from './face/FaceLogTab';
import FaceCompareTab from './face/FaceCompareTab';
import FaceSettingsTab from './face/FaceSettingsTab';
import './HRFace.css';

const TABS = [
  { key: 'enrol', label: 'Enrolment' },
  { key: 'devices', label: 'Devices' },
  { key: 'log', label: 'Activity' },
  { key: 'compare', label: 'Compare' },
  { key: 'settings', label: 'Settings' },
];

export default function HRFace({ employee }) {
  const [tab, setTab] = useState('enrol');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [confirmLive, setConfirmLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const isAdmin = employee?.role === 'admin' || employee?.role === 'super_admin';

  const load = useCallback(async () => {
    try { setStatus(await hr('/status')); setError(''); }
    catch (e) { setError(e.status === 404 ? 'The face module is not installed on the server yet.' : e.message); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const setLive = async (live) => {
    setBusy(true);
    try { await hr('/live', { method: 'PUT', body: { live } }); setConfirmLive(false); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const live = status?.punch_live === true;
  const today = status?.today || {};

  return (
    <div className="fc-root">
      <div className="fc-header">
        <div>
          <h1 className="fc-title">🙂 Face Attendance</h1>
          <p className="fc-sub">Kiosk check-in and check-out by face — for locations without GPS or signal, such as the underground service centre. Open <b>/kiosk</b> on the kiosk device and sign in with a device account created below.</p>
        </div>
      </div>

      {error && <div className="fc-callout fc-bad">{error}</div>}
      {status && !status.configured && (
        <div className="fc-callout fc-bad"><b>Not configured on the server.</b> Add <code>FACE_TEMPLATE_KEY</code> (64 hex characters) to the backend <code>.env</code> and restart. Enrolment and recognition are disabled until then.</div>
      )}

      {status && (
        <div className={`fc-mode ${live ? 'fc-mode-live' : 'fc-mode-test'}`}>
          <div className="fc-mode-main">
            <span className="fc-mode-dot" />
            <div>
              <div className="fc-mode-title">{live ? 'LIVE — face punches are written to attendance' : 'TEST MODE — face punches are recorded here only'}</div>
              <div className="fc-mode-sub">
                {live
                  ? 'The same lateness, status and late-worker credit rules as the phone app apply. A phone clock-in is never overwritten.'
                  : 'Nothing is written to attendance. Staff keep using the app; use the Compare tab to check the kiosk against the real records before going live.'}
              </div>
            </div>
          </div>
          <div className="fc-mode-side">
            <div className="fc-stats">
              <span><b>{status.enrolled?.enrolled ?? 0}</b> enrolled</span>
              <span><b>{status.devices?.online ?? 0}</b>/{status.devices?.active ?? 0} devices online</span>
              <span><b>{today.accepted ?? 0}</b> punches today</span>
            </div>
            {isAdmin ? (
              confirmLive ? (
                <div className="fc-confirm">
                  <span>{live ? 'Back to test mode?' : 'Start writing attendance?'}</span>
                  <button className={live ? 'fc-btn' : 'fc-btn fc-btn-red'} disabled={busy} onClick={() => setLive(!live)}>{live ? 'Yes, test mode' : 'Yes, go live'}</button>
                  <button className="fc-btn fc-btn-ghost" onClick={() => setConfirmLive(false)}>Cancel</button>
                </div>
              ) : (
                <button className="fc-btn fc-btn-ghost" onClick={() => setConfirmLive(true)}>{live ? 'Switch to test mode' : 'Go live…'}</button>
              )
            ) : (
              <span className="fc-muted">Only an admin can switch between test and live.</span>
            )}
          </div>
        </div>
      )}

      <div className="fc-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`fc-tab ${tab === t.key ? 'fc-tab-on' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === 'enrol' && <FaceEnrolTab configured={status ? status.configured : true} onChanged={load} />}
      {tab === 'devices' && <FaceDevicesTab />}
      {tab === 'log' && <FaceLogTab />}
      {tab === 'compare' && <FaceCompareTab live={live} />}
      {tab === 'settings' && <FaceSettingsTab />}
    </div>
  );
}
